/**
 * OTKS1E — qlty025 SummaryEmailOTKAdd JSON write contract.
 * Explicit allowlist — never for…in Formik / FormData.
 * Mutation envelope mirrors OtkOptionCatalogWriteContract (created|updated|noop).
 */

import { emitSummaryEmailOTKListRefresh } from './summaryEmailOTKListContract';

export const SUMMARY_EMAIL_OTK_MUTATIONS = Object.freeze({
  CREATED: 'created',
  UPDATED: 'updated',
  NOOP: 'noop',
});

/** Persisted allowlist for create/update JSON body. */
export const SUMMARY_EMAIL_OTK_WRITE_ALLOWLIST = Object.freeze([
  'type',
  'name',
  'status',
  'interval',
  'regularity',
  'email_time',
  'other_emails',
  'data',
  'employees',
]);

export const SUMMARY_EMAIL_OTK_FORBIDDEN_WRITE_KEYS = Object.freeze([
  'id',
  'created_at',
  'updated_at',
  'created_by_user_id',
  'updated_by_user_id',
  'created_by_user',
  'updated_by_user',
]);

export function parseSummaryEmailData(rawData) {
  if (!rawData) return {};
  if (typeof rawData === 'object') return rawData;
  if (typeof rawData !== 'string') return {};
  try {
    return JSON.parse(rawData);
  } catch (_e) {
    return {};
  }
}

export function initialValuesForSummaryEmailOTKForm(entity, type) {
  if (entity == null) {
    return {
      type: 'otk',
      name: '',
      data: [],
      dates: '',
      time_period: [],
      status: '',
      interval: '',
      email_time: '',
      regularity: '',
      employees: [],
      other_emails: [],
    };
  }
  const parsed = parseSummaryEmailData(entity.data);
  const values = {
    type: 'otk',
    name: entity.name ?? '',
    data: (parsed?.data ?? []).map((d) => ({ name: d?.name ?? d })).filter((d) => d.name),
    dates: parsed?.dates ?? '',
    time_period: (parsed?.time_period ?? []).map((tp) => ({ name: tp?.name ?? tp })).filter((tp) => tp.name),
    status: entity.status ?? '',
    interval: entity.interval ?? '',
    email_time: entity.email_time ?? '',
    regularity: entity.regularity ?? '',
    employees: (entity.employees || []).map((e) => ({ name: e.name, id: e.id })),
    other_emails: entity.other_emails
      ? String(entity.other_emails)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
  };
  if (type === 'edit' && entity.id != null) {
    values.id = entity.id;
  }
  return values;
}

/**
 * Explicit JSON body for POST create / update.
 * @returns {Record<string, unknown>}
 */
export function buildSummaryEmailOTKWritePayload(values) {
  const v = values && typeof values === 'object' ? values : {};
  const employees = Array.isArray(v.employees)
    ? v.employees.map((e) => (typeof e === 'object' ? Number(e.id) : Number(e))).filter((id) => id > 0)
    : [];
  const otherEmails = Array.isArray(v.other_emails)
    ? v.other_emails.map((s) => String(s).trim()).filter(Boolean)
    : typeof v.other_emails === 'string'
      ? v.other_emails.split(',').map((s) => s.trim()).filter(Boolean)
      : [];

  const dataPayload = {
    data: Array.isArray(v.data) ? v.data : [],
    dates: v.dates ?? '',
    time_period: Array.isArray(v.time_period) ? v.time_period : [],
  };

  return {
    type: 'otk',
    name: v.name ?? '',
    status: v.status ?? '',
    interval: v.interval ?? '',
    regularity: v.regularity ?? '',
    email_time: v.email_time ?? '',
    other_emails: otherEmails,
    employees,
    data: dataPayload,
  };
}

export function listWritePayloadKeys(payload) {
  return Object.keys(payload || {});
}

export function classifySummaryEmailOTKWriteResponse(response) {
  if (response == null || typeof response !== 'object') {
    return { kind: 'unknown', mutation: null };
  }
  const http = typeof response.__httpStatus === 'number' ? response.__httpStatus : null;
  const bodyStatus = typeof response.status === 'number' ? response.status : null;
  if ((http != null && http >= 400) || (bodyStatus != null && bodyStatus >= 400)) {
    return { kind: 'error', mutation: null };
  }
  const mutation = response.mutation;
  if (
    mutation === SUMMARY_EMAIL_OTK_MUTATIONS.CREATED ||
    mutation === SUMMARY_EMAIL_OTK_MUTATIONS.UPDATED ||
    mutation === SUMMARY_EMAIL_OTK_MUTATIONS.NOOP
  ) {
    return { kind: mutation, mutation };
  }
  // Legacy success without mutation discriminator (treat create via status 201).
  if (bodyStatus === 201 && response.data?.id) {
    return { kind: SUMMARY_EMAIL_OTK_MUTATIONS.CREATED, mutation: SUMMARY_EMAIL_OTK_MUTATIONS.CREATED };
  }
  if (bodyStatus === 200 && response.data?.id) {
    return { kind: 'unknown', mutation: null };
  }
  return { kind: 'unknown', mutation: mutation ?? null };
}

/**
 * Pure save-outcome planner (tests + editor).
 * No-op Save&Close follows catalog convention: close without success toast / list sync.
 */
export function planSummaryEmailOTKWriteOutcome({ response, closeAfter = false }) {
  const classified = classifySummaryEmailOTKWriteResponse(response);
  const row = response?.data ?? null;
  const canonical = row ? initialValuesForSummaryEmailOTKForm(row, 'edit') : null;

  if (
    classified.kind === SUMMARY_EMAIL_OTK_MUTATIONS.CREATED ||
    classified.kind === SUMMARY_EMAIL_OTK_MUTATIONS.UPDATED
  ) {
    return {
      outcome: classified.kind,
      shouldToast: true,
      shouldEmit: true,
      shouldClose: Boolean(closeAfter),
      listRefreshCount: 1,
      canonicalValues: canonical,
    };
  }

  if (classified.kind === SUMMARY_EMAIL_OTK_MUTATIONS.NOOP) {
    return {
      outcome: 'noop',
      shouldToast: false,
      shouldEmit: false,
      shouldClose: Boolean(closeAfter),
      listRefreshCount: 0,
      canonicalValues: canonical,
    };
  }

  return {
    outcome: classified.kind,
    shouldToast: false,
    shouldEmit: false,
    shouldClose: false,
    listRefreshCount: 0,
    canonicalValues: null,
  };
}

export function applySummaryEmailOTKWritePlan(plan, { toast, t, dispatch, selectedTabId }) {
  if (plan.shouldEmit) {
    emitSummaryEmailOTKListRefresh();
  }
  if (plan.shouldToast) {
    const detailKey =
      plan.outcome === SUMMARY_EMAIL_OTK_MUTATIONS.CREATED
        ? 'SummaryEmailOTKAdd.Toast.Created'
        : 'SummaryEmailOTKAdd.Toast.Updated';
    toast?.current?.show({
      severity: 'info',
      summary: t('Common.Confirmed') || 'Confirmed',
      life: 3000,
      detail: t(detailKey) || detailKey,
    });
  }
  if (plan.shouldClose && selectedTabId != null) {
    dispatch?.({ type: 'close_tab', id: selectedTabId });
  }
  return plan;
}
